import React, { useState, useEffect, useRef } from "react";
import "./index.css";
import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Skills from "./components/Skills";
import Experience from "./components/Experience";
import CurrentFocus from "./components/CurrentFocus";
import School from "./components/School";
import Footer from "./components/Footer";
import Blog from "./pages/Blog";
import ProjectDetail from "./components/ProjectDetail";
import { getProjectBySlug, Project } from "./utils/projects";

export default function App() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [initialBlogSlug, setInitialBlogSlug] = useState<string | null>(null);

  useEffect(() => {
    if (currentPage === "home") {
      document.title = "Tadej Gungl — Systems & Software Engineer";
    }
  }, [currentPage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 40);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const applyRoute = (page: string, slug?: string) => {
    setSelectedProject(null);
    if (page === "project" && slug) {
      getProjectBySlug(slug).then((project) => {
        if (project) {
          setSelectedProject(project);
          setCurrentPage("project");
        }
      });
      return;
    }
    setInitialBlogSlug(page === "blog" ? (slug || null) : null);
    setCurrentPage(page);
  };

  useEffect(() => {
    const handlePop = () => {
      const path = window.location.pathname;
      if (path.startsWith("/projects/")) {
        applyRoute("project", path.replace("/projects/", ""));
      } else if (path.startsWith("/blog")) {
        const slug = path === "/blog" ? undefined : path.replace("/blog/", "");
        applyRoute("blog", slug);
      } else {
        applyRoute("home");
      }
    };

    handlePop();
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const scrollToContact = () => {
    const el = scrollRef.current;
    const contact = document.getElementById("contact");
    if (el && contact) {
      const top = contact.getBoundingClientRect().top + el.scrollTop - 80;
      el.scrollTo({ top, behavior: "smooth" });
    }
  };

  const navigate = (page: string, slug?: string) => {
    if (page === "contact" || page === "cv") {
      if (currentPage === "home") {
        scrollToContact();
        return;
      }
      window.history.pushState({ page: "home" }, "", "/");
      setCurrentPage("home");
      setSelectedProject(null);
      setInitialBlogSlug(null);
      setTimeout(scrollToContact, 50);
      return;
    }

    if (page === "project" && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    const url = page === "blog" && slug ? `/blog/${slug}`
      : page === "blog" ? "/blog"
      : page === "project" && slug ? `/projects/${slug}`
      : "/";
    window.history.pushState({ page, slug }, "", url);
    applyRoute(page, slug);
  };

  const handleBackToPortfolio = () => {
    window.history.pushState({ page: "home" }, "", "/");
    setCurrentPage("home");
    setSelectedProject(null);
    setInitialBlogSlug(null);
    setTimeout(() => {
      const el = scrollRef.current;
      const projectsSection = document.getElementById("active-processes");
      if (el && projectsSection) {
        const top = projectsSection.getBoundingClientRect().top + el.scrollTop - 80;
        el.scrollTo({ top, behavior: "smooth" });
      }
    }, 50);
  };

  return (
    <main className="relative w-full h-screen overflow-hidden bg-Base transition-colors duration-300">
      <Header activePage={currentPage} onNavigate={(page) => navigate(page)} scrolled={scrolled} />
      
      <div ref={scrollRef} id="scroll-container" className="pt-16 w-full h-full overflow-y-auto scroll-smooth">
        {currentPage === "home" && (
          <>
            <Hero />
            <About />
            <Skills />
            <Experience />
            <School />
            <CurrentFocus onNavigate={navigate} />
            <Footer />
          </>
        )}
        {currentPage === "blog" && (
          <>
            <Blog initialSlug={initialBlogSlug} onNavigate={navigate} />
            <Footer />
          </>
        )}
        {currentPage === "project" && selectedProject && (
          <>
            <ProjectDetail
              project={selectedProject}
              onBack={handleBackToPortfolio}
              onNavigate={navigate}
            />
            <Footer />
          </>
        )}
      </div>
    </main>
  );
}
